const SCROLL_STICK_THRESHOLD = 10;
const SCROLL_OVERSHOOT = 500;

export interface ChatScrollManagerCallbacks {
  onStickyBottomChange: (value: boolean) => void;
  onScrolledToBottom: () => void;
  getStickyBottom: () => boolean;
}

export default class ChatScrollManager {
  private callbacks: ChatScrollManagerCallbacks;

  autoScrollInProgress = false;
  domElements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
    composerEl: HTMLDivElement | null;
  } = { scrollEl: null, contentEl: null, composerEl: null };

  constructor(callbacks: ChatScrollManagerCallbacks) {
    this.callbacks = callbacks;
  }

  setElements = (elements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
    composerEl: HTMLDivElement | null;
  }): void => {
    const prevScroll = this.domElements.scrollEl;
    const { scrollEl } = elements;

    if (prevScroll && prevScroll !== scrollEl) {
      prevScroll.removeEventListener("wheel", this.handleManualScroll);
      prevScroll.removeEventListener("touchstart", this.handleManualScroll);
      prevScroll.removeEventListener("scroll", this.handleScrollEvent);
    }

    if (scrollEl && scrollEl !== prevScroll) {
      scrollEl.addEventListener("wheel", this.handleManualScroll);
      scrollEl.addEventListener("touchstart", this.handleManualScroll);
      scrollEl.addEventListener("scroll", this.handleScrollEvent);
    }

    this.domElements = { ...elements };
  };

  scrollToBottom = (): void => {
    const el = this.domElements.scrollEl;
    if (!el) {
      return;
    }
    this.autoScrollInProgress = true;
    el.scrollTo({
      top: el.scrollHeight + SCROLL_OVERSHOOT,
      behavior: "instant"
    });
    requestAnimationFrame(() => {
      this.autoScrollInProgress = false;
    });
  };

  scrollToBottomAfterRender(): void {
    requestAnimationFrame(() => {
      this.scrollToBottom();
    });
  }

  restoreScrollIfNeeded = (): void => {
    if (this.callbacks.getStickyBottom()) {
      this.scrollToBottom();
    }
  };

  updateNewMessagePositioning = (): void => {
    const { scrollEl, contentEl, composerEl } = this.domElements;
    if (!scrollEl || !contentEl) {
      return;
    }

    const lastUserEl =
      contentEl.querySelector<HTMLElement>('[data-last="user"]');
    const composerHeight = composerEl?.clientHeight ?? 0;
    const userMsgHeight = lastUserEl?.offsetHeight ?? 0;
    const setHeight = scrollEl.clientHeight - composerHeight - userMsgHeight;

    if (setHeight > 0) {
      contentEl.style.setProperty(
        "--assistant-response-placeholder-height",
        `${setHeight.toFixed(1)}px`
      );
    }

    lastUserEl?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  dispose(): void {
    const el = this.domElements.scrollEl;
    if (el) {
      el.removeEventListener("wheel", this.handleManualScroll);
      el.removeEventListener("touchstart", this.handleManualScroll);
      el.removeEventListener("scroll", this.handleScrollEvent);
    }
  }

  private handleManualScroll = (): void => {
    if (this.callbacks.getStickyBottom()) {
      this.callbacks.onStickyBottomChange(false);
    }
  };

  private handleScrollEvent = (): void => {
    const el = this.domElements.scrollEl;
    if (!el || this.autoScrollInProgress) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const shouldStick = distanceFromBottom < SCROLL_STICK_THRESHOLD;
    const currentSticky = this.callbacks.getStickyBottom();

    if (shouldStick !== currentSticky) {
      this.callbacks.onStickyBottomChange(shouldStick);
    }

    if (shouldStick) {
      this.callbacks.onScrolledToBottom();
    }
  };
}
